import { ChangeDetectionStrategy, Component, inject, ViewEncapsulation, input, computed, model } from '@angular/core';
import { ILightWeightBusinessUnit } from '../models/access-right-filter-collection.model';
import { ILightWeightUser } from '../models/access-right-filter-collection.model';
import { AppTextService } from '../app-text';
import {
    FiDropdownItemAdditionalTextFn,
    FiDropdownItemDisabledFn,
    FiDropdownItemTitleFn,
} from '../fi-select-search/fi-select-search';
import { FiUserContactTypeEnum } from '../fi-user-contact-chip-grid-input/fi-user-contact-type.enum';
import {
    AutomatedActionRecipientActionType,
    AutomatedActionRecipientType,
} from './automated-action-recipient-select-enum';
import { IAccessRightFilterCollection } from '../models';
import { IAutomatedActionRecipient } from './automated-action-recipient-select.d';
import { INameIdObj } from '../fi-shared-interfaces';
import { FiAutomatedActionRecipientHelperService } from './automated-action-recipient-select-helper.service';
import { FiComponentsContainer } from '../fi-components-container';
@Component({
    selector: 'fi-automated-action-recipient-select',
    templateUrl: './automated-action-recipient-select.component.html',
    styleUrls: ['./automated-action-recipient-select.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-automated-action-recipient-select' },
    standalone: false,
    providers: [{ provide: FiComponentsContainer, useExisting: FiAutomatedActionRecipientSelectComponent }],
})
export class FiAutomatedActionRecipientSelectComponent extends FiComponentsContainer {
    actionType = input.required<AutomatedActionRecipientActionType>();
    recipient = model.required<IAutomatedActionRecipient>();
    accessRightFilterCollection = input.required<IAccessRightFilterCollection>();

    isDirectAssigneeAvailable = input<boolean>(true);
    // todo: create general enums that can be used for all recipient selects
    // and use them here instead of INameIdObj[]
    recipientTypeOptions = input.required<INameIdObj[]>();
    locationTypeOptions = input<INameIdObj[]>([]);
    //
    private readonly appTextService = inject(AppTextService);
    private readonly fiAutomatedActionRecipientHelperService = inject(FiAutomatedActionRecipientHelperService);

    protected isSharingItemDisabledFn: FiDropdownItemDisabledFn<ILightWeightUser | ILightWeightBusinessUnit> = (
        item: ILightWeightUser | ILightWeightBusinessUnit,
        _,
    ) => !item.IsActivated;

    protected getSharingItemTitleFn: FiDropdownItemTitleFn<ILightWeightUser | ILightWeightBusinessUnit> = (
        item: ILightWeightUser | ILightWeightBusinessUnit,
        _,
    ) => (!item.IsActivated ? this.appTextService.get('common.deactivated') : undefined);

    protected getOptionAdditionalClassFn: FiDropdownItemAdditionalTextFn<ILightWeightUser | ILightWeightBusinessUnit> =
        (item: ILightWeightUser | ILightWeightBusinessUnit, _) => (!item.IsActivated ? 'red-color' : undefined);

    protected isRolesRecipientType = computed(() => this.isRecipientType(AutomatedActionRecipientType.Roles));
    protected isLocalUnitRolesRecipientType = computed(() =>
        this.isRecipientType(AutomatedActionRecipientType.RolesLocal),
    );
    protected isUserRecipientType = computed(() => this.isRecipientType(AutomatedActionRecipientType.Users));
    protected isEmailRecipientType = computed(() => this.isRecipientType(AutomatedActionRecipientType.EmailAddress));
    protected isSMSRecipientType = computed(() => this.isRecipientType(AutomatedActionRecipientType.SMS));

    protected rolesSelectHintText = computed(() =>
        this.fiAutomatedActionRecipientHelperService.getRolesSelectHintText(
            this.actionType(),
            this.isLocalUnitRolesRecipientType(),
        ),
    );
    protected targetLabelText = computed(() =>
        this.fiAutomatedActionRecipientHelperService.getTargetLabelText(this.actionType()),
    );
    protected targetHintText = computed(() =>
        this.fiAutomatedActionRecipientHelperService.getTargetHintText(this.actionType()),
    );
    protected rolesLabelText = computed(() =>
        this.fiAutomatedActionRecipientHelperService.getRolesLabelText(this.actionType()),
    );
    protected unitsHintText = computed(() =>
        this.fiAutomatedActionRecipientHelperService.getUnitsHintText(this.actionType()),
    );
    protected usersLabelText = computed(() =>
        this.fiAutomatedActionRecipientHelperService.getUsersLabelText(this.actionType()),
    );
    protected usersSelectHintText = computed(() =>
        this.fiAutomatedActionRecipientHelperService.getUsersSelectHintText(this.actionType()),
    );

    protected recipientContactType = computed(() => {
        switch (this.recipient().RecipientType) {
            case AutomatedActionRecipientType.SMS:
                return FiUserContactTypeEnum.PhoneNumber;
            case AutomatedActionRecipientType.EmailAddress:
                return FiUserContactTypeEnum.Email;
        }
    });

    protected isRecipientLocationSelectionShown = computed(() => {
        return (
            this.actionType() === AutomatedActionRecipientActionType.CreateTask &&
            this.recipient().RecipientType === AutomatedActionRecipientType.RolesLocal
        );
    });

    protected updateRecipientField<K extends keyof IAutomatedActionRecipient>(
        field: K,
        value: IAutomatedActionRecipient[K],
        resetRecipientData: boolean = false,
    ) {
        if (resetRecipientData) {
            this.recipient.update((current) => ({
                ...current,
                [field]: value,
                UserIds: [],
                RoleIds: [],
                UnitIds: [],
                UserContacts: [],
            }));
        } else {
            this.recipient.update((current) => ({ ...current, [field]: value }));
        }
    }

    private isRecipientType(type: AutomatedActionRecipientType): boolean {
        return this.recipient().RecipientType === type;
    }
}
